import React from 'react';
import { ValidationError } from '../utils/formValidation';

interface ErrorDisplayProps {
  errors: ValidationError[];
  field?: string;
  section?: string;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ errors, field, section }) => {
  // Filter errors based on props
  let filteredErrors = errors;
  
  if (field) {
    filteredErrors = errors.filter(error => error.field === field);
  } else if (section) {
    filteredErrors = errors.filter(error => error.section === section);
  }
  
  if (filteredErrors.length === 0) {
    return null;
  }
  
  return (
    <div className="error-container">
      {filteredErrors.map((error, index) => (
        <div key={index} className="text-red-600 text-sm mt-1 flex items-center">
          <svg 
            className="w-4 h-4 mr-1 flex-shrink-0" 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path 
              fillRule="evenodd" 
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" 
              clipRule="evenodd" 
            />
          </svg>
          <span>{error.message}</span>
        </div>
      ))}
    </div>
  );
};

export default ErrorDisplay;

// Field-specific error component
interface FieldErrorProps {
  errors: ValidationError[];
  field: string;
  className?: string;
}

export const FieldError: React.FC<FieldErrorProps> = ({ errors, field, className = "" }) => {
  const fieldErrors = errors.filter(error => error.field === field);
  
  if (fieldErrors.length === 0) {
    return null;
  }
  
  return (
    <div className={`text-red-600 text-sm mt-1 ${className}`}>
      {fieldErrors.map((error, index) => (
        <div key={index} className="flex items-center">
          <svg 
            className="w-4 h-4 mr-1 flex-shrink-0" 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path 
              fillRule="evenodd" 
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" 
              clipRule="evenodd" 
            />
          </svg>
          <span>{error.message}</span>
        </div>
      ))}
    </div>
  );
};

// Section summary component
interface SectionErrorSummaryProps {
  errors: ValidationError[];
  section: string;
  isCollapsed?: boolean;
}

export const SectionErrorSummary: React.FC<SectionErrorSummaryProps> = ({ 
  errors, 
  section, 
  isCollapsed = false 
}) => {
  const sectionErrors = errors.filter(error => error.section === section);
  
  if (sectionErrors.length === 0) {
    return null;
  }
  
  return (
    <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
      <div className="flex items-center">
        <svg 
          className="w-5 h-5 text-red-400 mr-2" 
          fill="currentColor" 
          viewBox="0 0 20 20"
        >
          <path 
            fillRule="evenodd" 
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" 
            clipRule="evenodd" 
          />
        </svg>
        <h4 className="text-red-800 font-medium">
          {section} has {sectionErrors.length} error{sectionErrors.length > 1 ? 's' : ''}
        </h4>
      </div>
      
      {!isCollapsed && (
        <ul className="mt-2 text-red-700 text-sm space-y-1">
          {sectionErrors.map((error, index) => (
            <li key={index} className="flex items-start">
              <span className="mr-2">•</span>
              <span>{error.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Form-wide error summary
interface FormErrorSummaryProps {
  errors: ValidationError[];
  onScrollToSection?: (section: string) => void;
}

export const FormErrorSummary: React.FC<FormErrorSummaryProps> = ({ 
  errors, 
  onScrollToSection 
}) => {
  if (errors.length === 0) {
    return null;
  }
  
  // Group errors by section
  const errorsBySection = errors.reduce((acc, error) => {
    const section = error.section || 'General';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(error);
    return acc;
  }, {} as Record<string, ValidationError[]>);
  
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <div className="flex items-center mb-3">
        <svg 
          className="w-6 h-6 text-red-500 mr-2" 
          fill="currentColor" 
          viewBox="0 0 20 20"
        >
          <path 
            fillRule="evenodd" 
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" 
            clipRule="evenodd" 
          />
        </svg>
        <h3 className="text-red-800 font-semibold text-lg">
          Please fix {errors.length} error{errors.length > 1 ? 's' : ''} before submitting
        </h3>
      </div>
      
      <div className="space-y-3">
        {Object.entries(errorsBySection).map(([section, sectionErrors]) => (
          <div key={section} className="border-l-4 border-red-400 pl-3">
            <button
              className="text-red-800 font-medium hover:text-red-900 text-left"
              onClick={() => onScrollToSection?.(section)}
            >
              {section} ({sectionErrors.length} error{sectionErrors.length > 1 ? 's' : ''})
            </button>
            <ul className="mt-1 text-red-700 text-sm space-y-1">
              {sectionErrors.map((error, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{error.message}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};